import AddIcon from '@mui/icons-material/Add';
import {
    Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import * as ChatbotFlowService from '../../../../../../../../services/2talk/chatbot-flow.service.js';
import * as IntegrationUserVariableService from '../../../../../../../../services/integrationuservariable.service.js';

/**
 * VariableAutocompletePicker — componente reusável para seleção de variável do fluxo
 * com criação inline no cadastro oficial.
 *
 * Replica EXATAMENTE o padrão canônico do SetVariableInspector (06/10/2026, refactor 07/10):
 *   - Autocomplete SELECT-ONLY (sem freeSolo). Admin DEVE escolher variável existente.
 *   - Botão "+" ao lado abre modal inline → cria via POST /integration-user-variables (mesmo
 *     endpoint da tela de CRUD). Variável nova vira cidadã de 1ª classe no Mexx (aparece em
 *     outras caixas, auditável, deletável).
 *   - Depois de criar, auto-seleciona a nova no campo — zero fricção.
 *
 * Introduzido em 2026-10-13 para alinhar NativeActionInspector e MessageInspector (item #4.5)
 * ao padrão. Em 2026-10-13 estendido pra aceitar estado externo via props opcionais, pra
 * SetVariable e InteractiveList migrarem sem perder a semântica de "1 fetch compartilhado
 * entre N pickers" (SetVariable renderiza N pickers, 1 por operação).
 *
 * Modos de uso:
 *   1. STANDALONE (default): omitir `variables`/`loading`/`onReload` — o componente carrega e
 *      gerencia sua própria lista via useEffect. Ideal pra uso único (NativeAction, Message,
 *      InteractiveList — 1 picker por inspector).
 *   2. CONTROLLED: pai carrega 1x via `useVariablesCatalog()` e passa `variables`+`loading`+
 *      `onReload` pros N pickers filhos. Ideal pra listas (SetVariable — N operações = N
 *      pickers compartilhando 1 fetch). O botão "+" chama `onReload` após criar.
 */

interface VariableOption {
    id: number;
    name: string;
}

interface Props {
    value: string;
    onChange: (name: string) => void;
    /** Label do Autocomplete. */
    label?: string;
    /** Placeholder do input. */
    placeholder?: string;
    /** HelperText default (sobrescrito pelo warning "variável deletada"). */
    helperText?: string;
    /** Se true, mostra o botão "+" de criação inline. Default: true. */
    allowCreate?: boolean;
    /** Opcional: função que renderiza o helperText quando o nome persistido não existe mais na lista. */
    missingText?: (name: string) => string;
    /**
     * Opcional — lista externa de variáveis. Se passada (modo CONTROLLED), o componente NÃO
     * faz fetch próprio. Usada por `SetVariableInspector` que compartilha 1 fetch entre N pickers.
     */
    variables?: VariableOption[];
    /** Opcional — loading externo (idem CONTROLLED). Required se `variables` passado. */
    loading?: boolean;
    /**
     * Opcional — reload externo chamado após criar nova variável (idem CONTROLLED). Required se
     * `variables` passado — senão a lista fica stale e o auto-select falha porque o nome recém
     * criado ainda não está no array.
     */
    onReload?: () => Promise<void> | void;
}

// Tipo "USER" (admin-criado) — Id=2 no seed canônico. Mesma constante do SetVariableInspector.
const VARIABLE_TYPE_USER_ID = 2;

const VariableAutocompletePicker = (props: Props): ReactElement => {
    const {
        value,
        onChange,
        label,
        placeholder,
        helperText,
        allowCreate = true,
        missingText,
        variables: externalVariables,
        loading: externalLoading,
        onReload: externalReload,
    } = props;
    const intl = useIntl();

    // Modo CONTROLLED se qualquer uma das 3 props de estado foi passada. Elas são "tudo ou nada".
    const isControlled = externalVariables !== undefined;

    const [internalVariables, setInternalVariables] = useState<VariableOption[]>([]);
    const [internalLoading, setInternalLoading] = useState<boolean>(false);

    const [createOpen, setCreateOpen] = useState<boolean>(false);
    const [createName, setCreateName] = useState<string>('');
    const [createSaving, setCreateSaving] = useState<boolean>(false);
    const [createError, setCreateError] = useState<string | null>(null);

    // Fetch interno (modo STANDALONE). No modo CONTROLLED não é chamado.
    const loadVariablesInternal = useCallback(async () => {
        setInternalLoading(true);
        try {
            const resp: any = await (ChatbotFlowService as any).getPlaceholders();
            const flat: any[] = Array.isArray(resp?.data) ? resp.data : [];
            const list: VariableOption[] = flat
                .filter((p: any) => (p?.category ?? p?.Category) === 'variable')
                .map((p: any) => ({
                    id: Number(p?.id ?? p?.Id ?? 0),
                    name: String(p?.key ?? p?.Key ?? ''),
                }))
                .filter((v: VariableOption) => !!v.name)
                .sort((a: VariableOption, b: VariableOption) => a.name.localeCompare(b.name));
            setInternalVariables(list);
        } catch {
            setInternalVariables([]);
        } finally {
            setInternalLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isControlled) loadVariablesInternal();
    }, [isControlled, loadVariablesInternal]);

    // Resolve o estado efetivo (externo em CONTROLLED, interno em STANDALONE).
    const variables = isControlled ? externalVariables! : internalVariables;
    const loading = isControlled ? !!externalLoading : internalLoading;
    const reload = isControlled
        ? (externalReload ?? (async () => { /* no-op */ }))
        : loadVariablesInternal;

    const selectedVar = variables.find((v) => v.name === value) ?? null;
    // Warning "variável não existe" só pode aparecer DEPOIS que o fetch terminou. Antes do fetch,
    // variables=[] → qualquer var existente seria marcada "missing" por 1-2s até a resposta chegar.
    const isMissing = !loading && !!value && !selectedVar;

    const openCreateModal = () => {
        setCreateName('');
        setCreateError(null);
        setCreateOpen(true);
    };

    const closeCreateModal = () => {
        if (createSaving) return;
        setCreateOpen(false);
        setCreateError(null);
    };

    const handleCreateSave = async () => {
        const name = createName.trim();
        if (!name) {
            setCreateError(intl.formatMessage({
                id: 'varpicker.create.nameRequired',
                defaultMessage: 'Nome da variável é obrigatório.',
            }));
            return;
        }

        // Guard: variável com o MESMO nome já existe → seleciona a existente, fecha modal.
        const dup = variables.find((v) => v.name.toLowerCase() === name.toLowerCase());
        if (dup) {
            onChange(dup.name);
            closeCreateModal();
            return;
        }

        setCreateSaving(true);
        setCreateError(null);
        try {
            const output: any = await IntegrationUserVariableService.create({
                name,
                integrationuservariabletypeid: VARIABLE_TYPE_USER_ID,
            });

            if (!output?.success) {
                const msg = output?.errors?.[0]
                    ?? intl.formatMessage({ id: 'varpicker.create.failed', defaultMessage: 'Falha ao criar variável.' });
                setCreateError(String(msg));
                return;
            }

            await reload();
            onChange(name);
            closeCreateModal();
        } catch (err: any) {
            const apiMsg = err?.response?.data?.errors?.[0];
            setCreateError(String(apiMsg
                ?? intl.formatMessage({ id: 'varpicker.create.failed', defaultMessage: 'Falha ao criar variável.' })));
        } finally {
            setCreateSaving(false);
        }
    };

    const resolvedLabel = label ?? intl.formatMessage({ id: 'varpicker.label', defaultMessage: 'Variável' });
    const resolvedPlaceholder = placeholder ?? intl.formatMessage({
        id: 'varpicker.placeholder',
        defaultMessage: 'Escolha uma variável cadastrada',
    });

    return (
        <>
            <Stack direction="row" spacing={0.5} alignItems="flex-start">
                <Autocomplete<VariableOption, false, false, false>
                    sx={{ flex: 1 }}
                    size="small"
                    options={variables}
                    value={selectedVar}
                    loading={loading}
                    loadingText={intl.formatMessage({ id: 'varpicker.loading', defaultMessage: 'Carregando variáveis...' })}
                    noOptionsText={intl.formatMessage({
                        id: 'varpicker.empty',
                        defaultMessage: 'Nenhuma variável cadastrada — use o + ao lado para criar.',
                    })}
                    getOptionLabel={(o) => o?.name ?? ''}
                    isOptionEqualToValue={(a, b) => a?.name === b?.name}
                    disabled={loading}
                    onChange={(_, next) => onChange(next?.name ?? '')}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={resolvedLabel}
                            placeholder={loading
                                ? intl.formatMessage({ id: 'varpicker.loading', defaultMessage: 'Carregando variáveis...' })
                                : resolvedPlaceholder}
                            error={isMissing}
                            helperText={isMissing
                                ? (missingText
                                    ? missingText(value)
                                    : intl.formatMessage({
                                        id: 'varpicker.missing',
                                        defaultMessage: 'Variável "{name}" não existe mais no cadastro. Escolha outra ou crie uma nova.',
                                    }, { name: value }))
                                : helperText}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading
                                            ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
                                            : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
                {allowCreate && (
                    <Tooltip title={intl.formatMessage({
                        id: 'varpicker.create.tooltip',
                        defaultMessage: 'Criar nova variável no cadastro',
                    })}>
                        <span>
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={openCreateModal}
                                sx={{ mt: 0.5 }}
                                disabled={loading}
                            >
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Stack>

            {/* Modal "+ Nova variável" — padrão idêntico ao SetVariableInspector. */}
            <Dialog
                open={createOpen}
                onClose={closeCreateModal}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { maxHeight: '90vh', borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
                    {intl.formatMessage({ id: 'varpicker.create.title', defaultMessage: 'Nova variável' })}
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <div style={{ padding: '16px 20px' }}>
                        <Stack spacing={2}>
                            <TextField
                                label={intl.formatMessage({ id: 'varpicker.create.name', defaultMessage: 'Nome' })}
                                size="small"
                                fullWidth
                                autoFocus
                                value={createName}
                                onChange={(e) => setCreateName(e.target.value)}
                                placeholder="ex.: dados_cliente"
                                helperText={intl.formatMessage({
                                    id: 'varpicker.create.name.help',
                                    defaultMessage: 'Nome único — letras, números e underscore. Tipo = USER automaticamente.',
                                })}
                                disabled={createSaving}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !createSaving && createName.trim()) {
                                        e.preventDefault();
                                        handleCreateSave();
                                    }
                                }}
                            />
                            {createError && (
                                <Typography variant="caption" color="error">
                                    {createError}
                                </Typography>
                            )}
                        </Stack>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeCreateModal} disabled={createSaving}>
                        {intl.formatMessage({ id: 'varpicker.create.cancel', defaultMessage: 'Cancelar' })}
                    </Button>
                    <Button
                        onClick={handleCreateSave}
                        variant="contained"
                        disabled={createSaving || !createName.trim()}
                        startIcon={createSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
                    >
                        {intl.formatMessage({ id: 'varpicker.create.save', defaultMessage: 'Criar e selecionar' })}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default VariableAutocompletePicker;

/**
 * Hook companion do `VariableAutocompletePicker` — carrega o catálogo de variáveis UMA vez
 * e expõe `{ variables, loading, reload }` pros pickers filhos. Usado por inspectors que
 * renderizam uma LISTA de pickers (ex.: SetVariable, 1 picker por operação).
 *
 * Fetch dispara no mount. `reload()` re-busca manualmente (chamado após criar via botão "+").
 */
export function useVariablesCatalog() {
    const [variables, setVariables] = useState<VariableOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const resp: any = await (ChatbotFlowService as any).getPlaceholders();
            const flat: any[] = Array.isArray(resp?.data) ? resp.data : [];
            const list: VariableOption[] = flat
                .filter((p: any) => (p?.category ?? p?.Category) === 'variable')
                .map((p: any) => ({
                    id: Number(p?.id ?? p?.Id ?? 0),
                    name: String(p?.key ?? p?.Key ?? ''),
                }))
                .filter((v: VariableOption) => !!v.name)
                .sort((a: VariableOption, b: VariableOption) => a.name.localeCompare(b.name));
            setVariables(list);
        } catch {
            setVariables([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    return { variables, loading, reload };
}
